import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { showToast } from "vant";
import { Response } from "@/types/common";
import { i18n } from "@/i18n/index";
import {
  decryptBase64,
  decryptWithAes,
  encryptBase64,
  encryptWithAes,
  generateAesKey,
} from "@/utils/crypto";
import { decrypt, encrypt } from "@/utils/jsencrypt";

const { t } = i18n.global;
export const baseURL = process.env.VUE_APP_BASE_API;

const encryptHeader = 'X-Auth-Key';
const ENCRYPT_ENABLED = process.env.VUE_APP_ENCRYPT === 'true';

const getPartnerId = () =>
  localStorage.getItem('partnerId') ?? process.env.VUE_APP_PARTNER_ID ?? '1';

function decryptResponse(res: AxiosResponse): AxiosResponse {
  const keyStr =
    res.headers[encryptHeader] ||
    res.headers['x-auth-key'] ||
    res.headers['X-Auth-Key'];

  if (keyStr) {
    const base64Str = decrypt(keyStr);
    if (!base64Str) {
      throw new Error('RSA decrypt failed');
    }
    const aesKey = decryptBase64(base64Str.toString());
    const decryptedData = decryptWithAes(res.data, aesKey);
    res.data = JSON.parse(decryptedData);
  }

  return res;
}

const _axios: AxiosInstance = axios.create({
  baseURL: baseURL + "/app",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 50 * 1000,
});

_axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers['partnerId'] = getPartnerId();
  config.headers['Authorization'] = localStorage.getItem('token') ?? 'G';

  const clientId = localStorage.getItem('clientId');
  if (clientId) {
    config.headers['clientid'] = clientId;
  }

  const tenantId = localStorage.getItem('tenantId');
  if (tenantId) {
    config.headers['tenant-id'] = tenantId;
  }

  const language = document.querySelector("html")?.getAttribute("language");
  if (language) {
    config.headers["language"] = language;
  }

  const lang = document.querySelector("html")?.getAttribute("lang");
  if (lang) {
    config.headers["lang"] = lang;
  }

  if (ENCRYPT_ENABLED) {
    if (config.method === 'post' || config.method === 'put' || config.method === 'patch') {
      try {
        const aesKey = generateAesKey();
        const encryptedAesKey = encrypt(encryptBase64(aesKey));

        if (!encryptedAesKey) {
          return Promise.reject(new Error('Request encryption failed'));
        }

        config.headers[encryptHeader] = encryptedAesKey;

        if (typeof config.data === 'object') {
          config.data = encryptWithAes(JSON.stringify(config.data), aesKey);
        } else if (config.data) {
          config.data = encryptWithAes(config.data, aesKey);
        }
      } catch (e) {
        console.error('Request encryption error:', e);
        return Promise.reject(new Error('Request encryption failed'));
      }
    }
  }

  return config;
});

const handleSuccessResponse = (response: AxiosResponse) => {
  if (response.status === 200) {
    if (response.data.status == 200) {
      return Promise.resolve(response.data);
    } else if (response.data.code == 401) {
      location.replace("#/")
      localStorage.setItem('token', '')

      return Promise.reject({ handled: true, msg: t(response.data.msg), code: 401 });
    } else {
      return Promise.reject(t(response.data.msg) || "Erro do sistema");
    }
  } else {
    showToast("Erro do sistema");
    return Promise.reject(response);
  }
};

_axios.interceptors.response.use(
  (response: AxiosResponse) => {
    if (ENCRYPT_ENABLED) {
      try {
        decryptResponse(response);
      } catch (e) {
        console.error('Response decryption failed:', e);
        return Promise.reject({ msg: 'Decryption failed', code: 500 });
      }
    }

    return handleSuccessResponse(response);
  },
  (error) => {
    if (ENCRYPT_ENABLED && error.response) {
      try {
        decryptResponse(error.response);
        const data = error.response.data;
        return Promise.reject(data?.msg || data || error);
      } catch (e) {
        console.error('Response decryption failed:', e);
        return Promise.reject({ msg: 'Decryption failed', code: 500 });
      }
    }

    return Promise.reject(error);
  }
);

export const get = (url: string, params?: any): Promise<Response> =>
  _axios.get(url, { params });

export const post = (url: string, params?: any): Promise<Response> =>
  _axios.post(url, params);
